import { Fragment, h, Host } from "@stencil/core";
import { interval, Subject, takeUntil } from "rxjs";
import localI18n, { i18nInit } from "../../util/i18n";
import { getShouldBeIgnored } from "../../util/jwt-util/html-util/html-util";
import { JwtUtil } from "../../util/jwt-util/jwt-util";
export class SessionTimer {
    constructor() {
        this.currentLanguage = 'de';
        this.fontSize = 18;
        this.displayDate = '';
        this.displayDateMessage = '';
        this.displayDateMessageScreenreader = '';
        this.svgCirclePath = '';
        this.showMenuButton = false;
        this.showFlyout = false;
        this.isInvisible = false;
    }
    el;
    button;
    flyout;
    size = 0;
    durationInSec = 30 * 60;
    durationInMillis = this.durationInSec * 1000;
    deltaDeg = 360 / this.durationInSec;
    finished = new Subject();
    ticker;
    /**
     * Request a token event form the oiam-oauth-wc
     */
    sendTokenEventEmitter;
    /**
     * Request a session event form the oiam-oauth-wc
     */
    sendSessionEventEmitter;
    /**
     * Request bahfLanguage from HF via bahfSendLanguage
     */
    bahfSendLanguage;
    handleLanguageFromHeader(ev) {
        const language = ev?.detail?.activeLanguage?.code ?? 'de';
        i18nInit(language).then(() => {
            this.currentLanguage = language;
        });
    }
    closeFlyoutWhenTabbingOut(event) {
        if (event.key !== 'Tab') {
            return;
        }
        const path = event.composedPath();
        const flyout = this.el.shadowRoot.getElementById('headerFlyoutSessionTimer');
        if (!path.includes(flyout) && this.showFlyout) {
            this.hideFlyout();
        }
    }
    async componentWillLoad() {
        this.bahfSendLanguage.emit();
        await i18nInit(this.currentLanguage);
    }
    componentDidLoad() {
        this.size = this.fontSize / 2;
        this.sendTokenEventEmitter.emit();
    }
    componentDidRender() {
        this.button = this.el.shadowRoot.querySelector('#session-timer-button');
        this.flyout = this.el.shadowRoot.querySelector('#headerFlyoutSessionTimer');
    }
    calculateRemainingTime(tick) {
        let newDuration = this.durationInSec - tick;
        let newMinutes = 0;
        let newSeconds = newDuration;
        if (newDuration >= 60) {
            newMinutes = Math.floor(newDuration / 60);
            newSeconds = newDuration - newMinutes * 60;
        }
        this.displayDate = this.formatTimerDuration(newMinutes + 1, newSeconds);
        this.displayDateMessage = this.formatFlyoutDuration(newMinutes, newSeconds);
        this.displayDateMessageScreenreader = `${newMinutes}${localI18n.t('TIMER_REMAINING_TIME_1')}${newSeconds}${localI18n.t('TIMER_REMAINING_TIME_2')}`;
    }
    formatTimerDuration(minutes, seconds) {
        return minutes > 1
            ? `${this.padTime(minutes)}${localI18n.t('TIMER_TIMER_DURATION_1')}`
            : `${this.padTime(seconds)}${localI18n.t('TIMER_TIMER_DURATION_2')}`;
    }
    formatFlyoutDuration(newMinutes, newSeconds) {
        return `${this.padTime(newMinutes)} : ${this.padTime(newSeconds)}`;
    }
    padTime(time) {
        return String(time).padStart(2, '0');
    }
    calculateSvg(timeInSec) {
        let { dx, dy } = this.getCoordinates(timeInSec);
        if (this.isFirstHalf(timeInSec)) {
            return `M${this.size},${this.size} L${this.size},0 A${this.size},${this.size} 1 0,1 ${dx} ,${dy} z`;
        }
        if (this.isSecondHalf(timeInSec)) {
            return `M${this.size},${this.size} L${this.size},0 A${this.size},${this.size} 1 0,1 ${this.size} ,${this.size * 2} A${this.size},${this.size} 1 0,1 ${dx},${dy} z`;
        }
        this.finished.next(true);
        return this.getFinalSvgState();
    }
    isFirstHalf(timeInSec) {
        return timeInSec <= this.durationInSec / 2;
    }
    isSecondHalf(timeInSec) {
        return !this.isFirstHalf(timeInSec) && timeInSec < this.durationInSec;
    }
    getInitialSvgState() {
        let { dx, dy } = this.getCoordinates(0);
        return `M${this.size},${this.size} L${this.size},0 A${this.size},${this.size} 1 0,1 ${dx} ,${dy} z`;
    }
    getFinalSvgState() {
        return `M${this.size},${this.size} L${this.size},0 A${this.size},${this.size} 1 0,1 ${this.size} ,${this.size * 2} A${this.size},${this.size} 1 0,1 ${this.size},0 z`;
    }
    getCoordinates(timeInSec) {
        let deltaRad = this.toRadians(90 - this.deltaDeg * timeInSec);
        let gk = Math.sin(deltaRad) * this.size;
        let ak = Math.cos(deltaRad) * this.size;
        let dx = this.size + ak;
        let dy = this.size - gk;
        return { dx, dy };
    }
    toRadians(angle) {
        return angle * (Math.PI / 180);
    }
    getSvgStyle() {
        return {
            preserveAspectRatio: 'xMidYMid meet',
            viewBox: '0 0 100 100',
            width: `${this.fontSize + 4}px`,
            height: `${this.fontSize + 4}px`,
        };
    }
    getCircleStyle() {
        return {
            transform: 'translate(1px, 1px)',
        };
    }
    getPathStyle() {
        return {
            fill: 'white',
            width: 'fit-content',
            height: 'fit-content',
            transform: 'translate(1px, 1px)',
        };
    }
    getHeaderElemStyle() {
        return {
            width: 'fit-content',
        };
    }
    getSessionSvgStyle() {
        return {
            'width': 'fit-content',
            'padding-left': '.5em',
        };
    }
    onButtonClick() {
        this.toggleFlyout();
    }
    toggleFlyout() {
        if (this.showFlyout) {
            this.hideFlyout();
        }
        else {
            this.showFlyoutContent();
        }
    }
    showFlyoutContent() {
        this.flyout.classList.add('collapsed');
        this.flyout.classList.remove('collapse');
        this.button.classList.add('collapse');
        this.button.classList.remove('collapsed');
        this.button.ariaExpanded = 'true';
        this.showFlyout = !this.showFlyout;
    }
    hideFlyout() {
        this.flyout.classList.remove('collapsed');
        this.flyout.classList.add('collapse');
        this.button.classList.add('collapsed');
        this.button.classList.remove('collapse');
        this.button.ariaExpanded = 'false';
        this.showFlyout = !this.showFlyout;
    }
    handleClickEvent(event) {
        const path = event.composedPath();
        // This solves a bug. Other Teams are closing their flyouts by creating an additional click on their menu-items.
        // We need to ignore these additional Clicks. Otherwise, our flyout opens and then instantly closes.
        const shouldBeIgnored = getShouldBeIgnored(path);
        if (shouldBeIgnored) {
            return;
        }
        if (!path.includes(this.el) && this.showFlyout) {
            this.hideFlyout();
        }
    }
    handleKeyDown(ev) {
        if (ev.key === 'Escape' && this.showFlyout) {
            this.hideFlyout();
        }
    }
    handleTokenEvent(ev) {
        if (JwtUtil.validateJwt(ev).isValidJwt) {
            this.sendSessionEventEmitter.emit();
        }
        else {
            // we have to hide the component if there is an empty jwt (logout in other tab, browser back etc.)
            if (this.showFlyout) {
                this.hideFlyout();
            }
            this.finished.next(true);
            this.showMenuButton = false;
        }
    }
    handleSessionEvent(ev) {
        let durationInMillis = ev.detail.sessionEnd - Date.now();
        this.setInitialState();
        this.startTimer(ev.detail.sessionEnd);
        this.showMenuButton =
            this.getShowComponent(durationInMillis) &&
                durationInMillis <= this.durationInMillis &&
                durationInMillis > this.durationInMillis - 1000;
    }
    setInitialState() {
        this.resetTimer();
        this.displayDate = this.formatTimerDuration(30, 0);
        this.displayDateMessage = this.formatFlyoutDuration(30, 0);
        this.svgCirclePath = this.getInitialSvgState();
    }
    resetTimer() {
        this.finished.next(true);
        this.ticker = interval(1000);
    }
    startTimer(sessionEnd) {
        let deltaInSec = 0;
        let isConfigured = false;
        this.ticker.pipe(takeUntil(this.finished)).subscribe((tick) => {
            let durationInMillis = sessionEnd - Date.now();
            this.showMenuButton = this.getShowComponent(durationInMillis);
            if (!isConfigured) {
                deltaInSec = Math.ceil((this.durationInMillis - durationInMillis) / 1000) + tick;
                isConfigured = true;
            }
            if (this.showMenuButton) {
                this.setNextSessionState(deltaInSec, tick);
            }
        });
    }
    getShowComponent(durationInMillis) {
        if (durationInMillis > 0) {
            return durationInMillis <= this.durationInMillis;
        }
        return false;
    }
    setNextSessionState(deltaInSec, tick) {
        this.svgCirclePath = this.calculateSvg(tick + deltaInSec);
        this.calculateRemainingTime(tick + deltaInSec);
    }
    render() {
        if (this.showMenuButton && !this.isInvisible) {
            return (h(Host, { key: 'a3b6b9738edcc4e93cea7a643769b1cd1af523da', class: 'd-block rounded' }, h("div", { key: 'bb7a9c37b464b0d06dd5dcae6f227111f1a3d5a7', class: "ba-header-element" }, this.getSessionTimer(), this.getFlyOut())));
        }
    }
    getSessionTimer() {
        return (h("button", { id: "session-timer-button", class: "ba-menu-entry ba-timer collapsed", title: localI18n.t('TIMER_1'), type: "button", "data-bs-toggle": "collapse", onClick: () => this.onButtonClick(), "data-bs-target": "#headerFlyoutSessionTimer", "aria-expanded": "false", "aria-controls": "headerFlyoutSessionTimer", style: this.getHeaderElemStyle() }, h("svg", { class: "session-svg", style: this.getSvgStyle() }, h("circle", { class: "circle", style: this.getCircleStyle(), cx: this.size, cy: this.size, r: this.size + 1 }), h("path", { style: this.getPathStyle(), d: this.svgCirclePath })), h("span", { style: this.getSessionSvgStyle() }, this.displayDate)));
    }
    getFlyOut() {
        return (h("div", { class: "ba-flyout-container ba-flyout-timer" }, h("div", { id: "headerFlyoutSessionTimer", class: "ba-flyout-collapse collapse", "data-bs-parent": ".ba-header" }, h("div", { class: "ba-flyout ba-menu-container", "aria-labelledby": "headingTimer", style: { maxHeight: '764px' } }, h("h2", { class: "h4", id: "headingTimer" }, localI18n.t('FLYOUT_HEADLINE')), h("hr", { "aria-hidden": "true" }), this.getCurrentFlyoutSessionTime(), this.getSessionTimeInfo(), this.getGeneralSessionInfo()))));
    }
    getCurrentFlyoutSessionTime() {
        return (
        // @ts-ignore
        h(Fragment, null, h("div", { "aria-hidden": "true", class: "d-flex flex-column align-items-center mt-2" }, h("p", { class: "h2 m-0" }, this.displayDateMessage), h("p", { class: "ba-microcopy" }, "\u00A0\u00A0\u00A0", localI18n.t('MINUTEN'), "\u00A0\u00A0\u00A0", localI18n.t('SEKUNDEN'))), h("p", { class: "sr-only" }, this.displayDateMessageScreenreader)));
    }
    getSessionTimeInfo() {
        return h("p", { class: "ba-accent ba-accent-hint mb-2" }, localI18n.t('FLYOUT_WARNING'));
    }
    getGeneralSessionInfo() {
        return h("p", { class: "mb-0" }, localI18n.t('FLYOUT_MIN_5_REMINDER'));
    }
    static get is() { return "session-timer"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["session-timer.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["session-timer.css"]
        };
    }
    static get properties() {
        return {
            "isInvisible": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "\uD83E\uDDD9\u200D\u2642\uFE0F Guard for rendering. UseCase no visibleHeader is present, but all should work."
                },
                "attribute": "is-invisible",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "currentLanguage": {},
            "fontSize": {},
            "displayDate": {},
            "displayDateMessage": {},
            "displayDateMessageScreenreader": {},
            "svgCirclePath": {},
            "showMenuButton": {},
            "showFlyout": {}
        };
    }
    static get events() {
        return [{
                "method": "sendTokenEventEmitter",
                "name": "oiamSendTokenEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Request a token event form the oiam-oauth-wc"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "sendSessionEventEmitter",
                "name": "oiamSendSessionEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Request a session event form the oiam-oauth-wc"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "bahfSendLanguage",
                "name": "bahfSendLanguage",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Request bahfLanguage from HF via bahfSendLanguage"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "bahfLanguage",
                "method": "handleLanguageFromHeader",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keyup",
                "method": "closeFlyoutWhenTabbingOut",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "click",
                "method": "handleClickEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "oiamTokenEvent",
                "method": "handleTokenEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "oiamSessionEvent",
                "method": "handleSessionEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
